import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, Truck, Save, Package, MapPin, User, Mail, Phone, Gift, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value, icon: Icon }) => (
  <div>
    <p className="font-mono text-[10px] tracking-[0.3em] text-rose flex items-center gap-2 mb-1">
      {Icon && <Icon size={12} />} {label}
    </p>
    <p className="text-off-white text-sm break-words">{value || "—"}</p>
  </div>
);

const AdminOrderDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("whipitup_admin_token") : null;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");
  const [notes, setNotes] = useState("");
  const [fulfilled, setFulfilled] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/orders/${sessionId}`, { headers: authHeader });
        setOrder(data);
        setTracking(data.tracking_number || "");
        setCarrier(data.tracking_carrier || "");
        setNotes(data.notes || "");
        setFulfilled(!!data.fulfilled);
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.removeItem("whipitup_admin_token");
          navigate("/admin/login");
        }
      } finally { setLoading(false); }
    })();
  }, [sessionId, token, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/orders/${sessionId}`,
        { fulfilled, notes, tracking_number: tracking || null, tracking_carrier: carrier || null },
        { headers: authHeader }
      );
      const { data } = await axios.get(`${API}/admin/orders/${sessionId}`, { headers: authHeader });
      setOrder(data);
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-charcoal flex items-center justify-center">
        <div className="flex items-center gap-3 text-off-white/60">
          <Loader2 size={20} className="animate-spin text-rose" />
          <span className="font-mono text-[11px] tracking-[0.25em]">LOADING ORDER…</span>
        </div>
      </main>
    );
  }
  if (!order) return null;

  const ship = order.shipping_address || {};

  return (
    <main className="min-h-screen bg-charcoal pt-8" data-testid="admin-order-detail">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="py-8 border-b border-off-white/10">
          <Link to="/admin" className="font-mono text-[10px] tracking-[0.3em] text-off-white/50 hover:text-rose transition-colors-only flex items-center gap-2 mb-4" data-testid="back-to-admin">
            <ArrowLeft size={12} /> ADMIN
          </Link>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// ORDER · {order.payment_status?.toUpperCase()}</p>
              <h1 className="font-serif-display italic text-4xl md:text-5xl text-off-white leading-[0.95]">
                ${order.amount?.toFixed(2)}
              </h1>
              <p className="font-mono text-[10px] tracking-[0.25em] text-off-white/40 mt-2 break-all">{order.session_id}</p>
              <p className="font-mono text-[10px] tracking-[0.25em] text-off-white/50 mt-1">
                {order.created_at ? new Date(order.created_at).toLocaleString() : ""}
              </p>
            </div>
            {order.fulfilled ? (
              <span className="font-mono text-[10px] tracking-[0.25em] px-3 py-1.5 bg-rose/20 text-rose border border-rose/40 flex items-center gap-2">
                <CheckCircle2 size={14} /> SHIPPED
              </span>
            ) : (
              <span className="font-mono text-[10px] tracking-[0.25em] px-3 py-1.5 border border-off-white/20 text-off-white/70 flex items-center gap-2">
                <Package size={14} /> TO SHIP
              </span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 py-8">
          {/* Customer + shipping */}
          <div className="space-y-6">
            <div className="border border-off-white/10 p-6 bg-off-white/[0.02]">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-4">// CUSTOMER</p>
              <div className="space-y-4">
                <Field icon={User} label="NAME" value={order.customer_name} />
                <Field icon={Mail} label="EMAIL" value={order.customer_email} />
                <Field icon={Phone} label="PHONE" value={order.customer_phone} />
              </div>
            </div>

            <div className="border border-off-white/10 p-6 bg-off-white/[0.02]" data-testid="shipping-address">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-4 flex items-center gap-2"><MapPin size={12} /> SHIPPING ADDRESS</p>
              {ship.line1 ? (
                <div className="space-y-1 text-off-white text-sm leading-relaxed">
                  <p>{ship.line1}</p>
                  {ship.line2 && <p>{ship.line2}</p>}
                  <p>{[ship.city, ship.state, ship.postal_code].filter(Boolean).join(", ")}</p>
                  <p className="text-off-white/60">{ship.country}</p>
                </div>
              ) : (
                <p className="text-off-white/50 text-sm">No shipping address captured.</p>
              )}
            </div>

            {order.reward_code && (
              <div className="border border-rose/40 bg-rose/5 p-6" data-testid="reward-applied">
                <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2 flex items-center gap-2"><Gift size={12} /> LOYALTY REWARD APPLIED</p>
                <p className="font-serif-display italic text-xl text-off-white">{order.reward_code}</p>
                <p className="text-off-white/60 text-xs mt-1">Include one 2 oz Body Oil at no charge.</p>
              </div>
            )}
          </div>

          {/* Items + fulfillment */}
          <div className="space-y-6">
            <div className="border border-off-white/10 p-6 bg-off-white/[0.02]" data-testid="order-items">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-4">// ITEMS ({order.items?.length})</p>
              <ul className="space-y-2">
                {order.items?.map((it, i) => (
                  <li key={i} className="flex justify-between text-off-white/85 border-b border-off-white/10 pb-2 last:border-b-0">
                    <span className="text-sm">{it.qty} × {it.name}</span>
                    <span className="font-mono text-off-white/60 text-sm">${it.line_total?.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-off-white/10 space-y-1 text-sm">
                <div className="flex justify-between text-off-white/60"><span>Subtotal</span><span className="font-mono">${order.subtotal?.toFixed(2)}</span></div>
                <div className="flex justify-between text-off-white/60"><span>Shipping</span><span className="font-mono">${order.shipping?.toFixed(2)}</span></div>
                <div className="flex justify-between text-off-white font-display text-lg mt-2"><span>Total</span><span>${order.amount?.toFixed(2)}</span></div>
              </div>
            </div>

            <div className="border border-off-white/10 p-6 bg-off-white/[0.02]" data-testid="fulfillment-form">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-4 flex items-center gap-2"><Truck size={12} /> FULFILLMENT</p>
              <div className="space-y-4">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-off-white/50 mb-2">TRACKING CARRIER</p>
                  <input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="USPS · UPS · FedEx"
                    className="w-full h-11 bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-4 text-off-white transition-colors-only"
                    data-testid="carrier-input"
                  />
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-off-white/50 mb-2">TRACKING NUMBER</p>
                  <input value={tracking} onChange={(e) => setTracking(e.target.value)}
                    className="w-full h-11 bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-4 text-off-white transition-colors-only"
                    data-testid="tracking-input"
                  />
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-off-white/50 mb-2">INTERNAL NOTES</p>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                    className="w-full bg-off-white/[0.04] border border-off-white/20 focus:border-rose outline-none px-4 py-3 text-off-white transition-colors-only"
                    data-testid="notes-input"
                  />
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={fulfilled} onChange={(e) => setFulfilled(e.target.checked)} data-testid="fulfilled-checkbox" />
                  <span className="font-mono text-[11px] tracking-[0.25em] text-off-white">MARK AS SHIPPED</span>
                </label>
                <button
                  onClick={save}
                  disabled={saving}
                  className="w-full h-12 bg-rose text-charcoal font-mono text-[11px] tracking-[0.25em] font-semibold hover:bg-off-white transition-colors-only disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="save-order-btn"
                >
                  <Save size={14} /> {saving ? "SAVING…" : "SAVE CHANGES"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AdminOrderDetail;
